import { useEffect, useRef, useState } from "react";

const benefits = [
  { icon: "ri-calendar-check-line", text: "Free Initial Consultation" },
  { icon: "ri-settings-3-line", text: "Customized Solutions for Your Needs" },
  { icon: "ri-timer-flash-line", text: "Fast Response Within 24 Hours" },
  { icon: "ri-shield-check-line", text: "100% Confidential & Secure" },
];

const contactInfo = [
  { icon: "ri-phone-line", text: "786-461-2818", href: "tel:+17864612818" },
  { icon: "ri-phone-line", text: "305-733-2170", href: "tel:+13057332170" },
  { icon: "ri-mail-line", text: "admin@mtcassociate.com", href: "mailto:admin@mtcassociate.com" },
  { icon: "ri-map-pin-line", text: "Remote-Friendly — Serving Clients Nationwide", href: null },
];

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const [phoneValue, setPhoneValue] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const validateEmail = (value: string): string => {
    if (!value) return "";
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return re.test(value) ? "" : "Please enter a valid email address.";
  };

  const validatePhone = (value: string): string => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    return digits.length === 10 ? "" : "Please enter a valid 10-digit phone number.";
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhoneValue(formatted);
    if (phoneError) setPhoneError(validatePhone(formatted));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const emailVal = data.get("email") as string;
    const phoneVal = data.get("phone") as string;
    const messageVal = data.get("message") as string;

    const eErr = validateEmail(emailVal);
    const pErr = validatePhone(phoneVal);

    if (eErr) setEmailError(eErr);
    if (pErr) setPhoneError(pErr);
    if (eErr || pErr) return;

    if (messageVal && messageVal.length > 500) {
      setStatus("error");
      return;
    }

    const encoded = new URLSearchParams();
    data.forEach((value, key) => encoded.append(key, value.toString()));
    setStatus("sending");
    try {
      const res = await fetch("https://readdy.ai/api/form/d71e91euimmp87kg1g50", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encoded.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
        setPhoneValue("");
        setEmailError("");
        setPhoneError("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const inputClass =
    "w-full px-4 py-3 rounded-md border bg-white text-charcoal-800 text-sm placeholder-charcoal-500 focus:outline-none transition-colors duration-200";
  const inputValid = "border-charcoal-200 focus:border-forest-500";
  const inputInvalid = "border-red-400 focus:border-red-500";

  return (
    <section id="contact" ref={ref} className="bg-white py-10 md:py-24 lg:py-28 px-4 md:px-6">
      <div
        className={`max-w-6xl mx-auto transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 rounded-xl overflow-hidden border border-charcoal-100">
          {/* Left Panel — Charcoal dark */}
          <div className="bg-charcoal-900 px-4 py-7 sm:px-8 sm:py-10 md:px-10 md:py-14 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2 md:mb-4">
                <div className="w-6 h-0.5 bg-forest-400" />
                <span className="text-forest-400 text-xs font-semibold tracking-[0.25em] uppercase">
                  Get In Touch
                </span>
              </div>
              <h2 className="font-cormorant text-xl md:text-3xl lg:text-4xl font-semibold text-white mb-3 md:mb-5 leading-snug">
                Let&apos;s Discuss Your Financial Needs
              </h2>
              <p className="text-charcoal-200 text-xs md:text-sm leading-relaxed mb-5 md:mb-10">
                Schedule a free consultation or simply send us a message. We&apos;ll
                get back to you within one business day.
              </p>

              {/* Benefits */}
              <ul className="space-y-2.5 md:space-y-4 mb-6 md:mb-12">
                {benefits.map((b) => (
                  <li key={b.text} className="flex items-center gap-2.5 md:gap-3">
                    <div className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-md bg-forest-600 flex-shrink-0">
                      <i className={`${b.icon} text-white text-xs`} />
                    </div>
                    <span className="text-charcoal-200 text-xs">{b.text}</span>
                  </li>
                ))}
              </ul>

              {/* Contact Info */}
              <ul className="space-y-2 md:space-y-3">
                {contactInfo.map((c) => (
                  <li key={c.text} className="flex items-start gap-2.5 md:gap-3">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className={`${c.icon} text-forest-400 text-xs md:text-sm`} />
                    </div>
                    {c.href ? (
                      <a
                        href={c.href}
                        className="text-charcoal-200 text-xs hover:text-forest-300 transition-colors cursor-pointer"
                      >
                        {c.text}
                      </a>
                    ) : (
                      <span className="text-charcoal-200 text-xs">{c.text}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 md:mt-10">
              <p className="text-charcoal-500 text-xs">
                &copy; {new Date().getFullYear()} MTC Associates. All rights reserved.
              </p>
            </div>
          </div>

          {/* Right Panel — Form */}
          <div className="bg-charcoal-50 px-4 py-7 sm:px-8 sm:py-10 md:px-10 md:py-14">
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-4 py-8">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-forest-100">
                  <i className="ri-check-line text-forest-600 text-xl md:text-2xl" />
                </div>
                <h3 className="font-cormorant text-xl md:text-2xl font-semibold text-charcoal-900">Message Received!</h3>
                <p className="text-charcoal-500 text-sm max-w-xs">
                  Thank you for reaching out. We&apos;ll be in touch within one
                  business day.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-4 text-forest-600 text-sm font-medium hover:underline cursor-pointer"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-us-form"
                onSubmit={handleSubmit}
                className="space-y-4 md:space-y-5"
              >
                <div className="mb-3 md:mb-6">
                  <h3 className="font-cormorant text-lg md:text-2xl font-semibold text-charcoal-900 mb-1">
                    Send Us a Message
                  </h3>
                  <p className="text-charcoal-500 text-xs">We&apos;ll respond within one business day.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-charcoal-700 text-xs font-semibold mb-1.5 md:mb-2">
                      First Name <span className="text-forest-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="Jane"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-charcoal-700 text-xs font-semibold mb-1.5 md:mb-2">
                      Last Name <span className="text-forest-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Smith"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-charcoal-700 text-xs font-semibold mb-1.5 md:mb-2">
                    Email Address <span className="text-forest-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="jane@example.com"
                    onChange={() => { if (emailError) setEmailError(""); }}
                    onBlur={(e) => setEmailError(validateEmail(e.target.value))}
                    className={`${inputClass} ${emailError ? inputInvalid : inputValid}`}
                  />
                  {emailError && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line" />
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-charcoal-700 text-xs font-semibold mb-1.5 md:mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={phoneValue}
                    onChange={handlePhoneChange}
                    onBlur={() => setPhoneError(validatePhone(phoneValue))}
                    placeholder="(786) 000-0000"
                    className={`${inputClass} ${phoneError ? inputInvalid : inputValid}`}
                  />
                  {phoneError && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line" />
                      {phoneError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-charcoal-700 text-xs font-semibold mb-1.5 md:mb-2">
                    Company Name <span className="text-charcoal-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    placeholder="Your Company Ltd."
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="block text-charcoal-700 text-xs font-semibold mb-1.5 md:mb-2">
                    Message <span className="text-forest-600">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us how we can help you..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-right text-charcoal-500 text-xs mt-1">
                    {charCount}/500
                  </p>
                </div>

                {status === "error" && (
                  <p className="text-red-500 text-xs">
                    Something went wrong. Please check your message and try again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white py-3 md:py-3.5 rounded-md text-sm font-semibold transition-colors duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  {status === "sending" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>

                <p className="text-center text-charcoal-500 text-xs">
                  We respect your privacy and will never share your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
